import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, Phone, Loader2, DollarSign, Clock, Gift, ArrowRight, ArrowLeft, ShieldCheck, Check,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import SwipeOpinionLogo from '../components/brand/SwipeOpinionLogo';
import { sendPublicPhoneOtp, verifyPublicPhoneOtp } from '../lib/api';
import { supabase } from '../lib/supabase';
import { createTypingCadenceTracker, type TypingCadenceData } from '../lib/typingCadence';
import { API_BASE } from '../lib/apiBase';

// Shared focus-visible treatment so every interactive control shows a keyboard
// focus ring (inputs already carry their own focus ring).
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5B4FE8] focus-visible:ring-offset-2';

// The three proof rows. Shared between the mobile intro (white cards) and the
// desktop left panel (white-on-translucent on the gradient).
const PROOF: { Icon: LucideIcon; text: string }[] = [
  { Icon: DollarSign, text: '$0.50–$3 for most surveys' },
  { Icon: Clock, text: 'Most take under 10 minutes' },
  { Icon: Gift, text: 'Amazon, Visa, Target & more' },
];

// ---------------------------------------------------------------------------
// Tabbed marketing landing — DESKTOP LEFT PANEL ONLY.
//
// Content tabs (logged-out pitch), NOT navigation: clicking swaps the hero copy
// below; no routing, no URL change. The signup form (right panel) is untouched
// and stays functional across every tab. Mobile keeps its two-step intro flow
// (no tabs) but shares the Home headline/subhead so the message agrees.
// ---------------------------------------------------------------------------

type LandingTabKey = 'home' | 'discover' | 'rewards' | 'impact' | 'trust';

interface LandingTab {
  key: LandingTabKey;
  label: string;
  headline: string;
  subhead: string;
  /** Home reuses the existing PROOF rows as supporting content. */
  useProof?: boolean;
  /** Discover / Impact / Trust: checkmark bullet list. */
  bullets?: string[];
  /** Rewards: styled text chips (NOT logo images). */
  brandChips?: string[];
}

const HOME_HEADLINE = 'Your opinion moves things forward.';
const HOME_SUBHEAD =
  'Share your thoughts, discover new products, and earn real rewards for the opinions you already have.';

const LANDING_TABS: LandingTab[] = [
  {
    key: 'home',
    label: 'Home',
    headline: HOME_HEADLINE,
    subhead: HOME_SUBHEAD,
    useProof: true,
  },
  {
    key: 'discover',
    label: 'Discover',
    headline: 'Thousands of opportunities to share your opinion.',
    subhead:
      'From quick polls to in-depth research, every opportunity is matched to your profile so your time is spent on studies that matter.',
    bullets: ['Personalized opportunities', 'New studies every day', 'Most take under 10 minutes'],
  },
  {
    key: 'rewards',
    label: 'Rewards',
    headline: 'Earn rewards your way.',
    subhead:
      'Every completed study earns points you can redeem for popular gift cards and other rewards. No confusing systems—just straightforward rewards for your time.',
    brandChips: ['Amazon', 'Visa', 'and more'],
  },
  {
    key: 'impact',
    label: 'Impact',
    headline: 'Your opinion shapes what comes next.',
    subhead:
      'Companies rely on real people—not algorithms—to improve products, services, advertising, and experiences. Every response helps someone make a better decision.',
    bullets: ['Influence future products', 'Improve customer experiences', 'Help brands understand real people'],
  },
  {
    key: 'trust',
    label: 'Trust',
    headline: 'Built to be worth your trust.',
    subhead:
      'No hidden rules, no disappearing points, no dead-ends. You always know where your points stand, why a study didn\'t match, and exactly what you\'ve earned.',
    bullets: [
      'Transparent points — always know your balance and what\'s pending',
      'Honest about fit — clear answers when a study isn\'t right for you',
      'Real rewards, real support — a person behind every question',
    ],
  },
];

// ---------------------------------------------------------------------------
// Phone Login Section
// ---------------------------------------------------------------------------

function formatPhone(digits: string): string {
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function PhoneLoginSection({
  navigate,
  compact = false,
}: {
  navigate: ReturnType<typeof useNavigate>;
  // compact = the desktop split's side-by-side pill (no top margin, short label).
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [digits, setDigits] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneNumber = `+1${digits}`;

  async function handleSendCode() {
    setLoading(true);
    setError(null);
    try {
      await sendPublicPhoneOtp(phoneNumber);
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    }
    setLoading(false);
  }

  async function handleVerify() {
    setLoading(true);
    setError(null);
    try {
      const result = await verifyPublicPhoneOtp(phoneNumber, otp);
      if (result.session) {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });

        // UA attribution
        try {
          const { attributeSession } = await import('../lib/ua');
          await attributeSession(result.session.access_token);
        } catch { /* non-fatal */ }

        // Fraud signal collection for new members
        if (result.isNewUser) {
          void import('../lib/deviceFingerprint').then(({ collectDeviceFingerprint, collectWebRTCLeaks }) => {
            collectDeviceFingerprint().then(fp => {
              fetch(`${API_BASE}/api/fraud/fingerprint`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${result.session!.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ fingerprintHash: fp.hash, components: fp.components, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
              }).catch(() => {});
            }).catch(() => {});
            collectWebRTCLeaks().then(leakedIps => {
              if (leakedIps.length > 0) {
                fetch(`${API_BASE}/api/fraud/webrtc-signal`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${result.session!.access_token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ leakedIps }),
                }).catch(() => {});
              }
            }).catch(() => {});
          }).catch(() => {});
        }

        // Check onboarding and navigate
        try {
          const meRes = await fetch(`${API_BASE}/api/me`, {
            headers: { Authorization: `Bearer ${result.session.access_token}` },
          });
          if (meRes.ok) {
            const me = await meRes.json();
            navigate(me.onboarding_complete ? '/dashboard' : '/onboarding', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
        } catch {
          navigate('/onboarding', { replace: true });
        }
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    }
    setLoading(false);
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`w-full py-3 ${compact ? 'px-4' : 'mt-3 px-6'} border-2 border-[#E5E7EB] text-[#111827] font-medium rounded-full hover:bg-gray-50 transition flex items-center justify-center gap-2 ${FOCUS_RING}`}
      >
        <Phone className="w-5 h-5" />
        {compact ? 'Phone' : 'Sign in with phone number'}
      </button>
    );
  }

  if (codeSent) {
    return (
      <div className="mt-4 space-y-3">
        <p className="text-sm text-[#6B7280] text-center">Enter the code sent to {formatPhone(digits)}</p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="w-full text-center text-xl tracking-[0.4em] font-mono border border-[#E5E7EB] rounded-xl px-4 py-3 focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
        />
        {error && <p className="text-[#EF4444] text-sm text-center">{error}</p>}
        <button
          onClick={() => void handleVerify()}
          disabled={otp.length < 6 || loading}
          className={`w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition flex items-center justify-center gap-2 ${FOCUS_RING}`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> : null}
          Verify
        </button>
        <button onClick={() => { setCodeSent(false); setOtp(''); setError(null); }} className={`w-full text-sm text-[#6B7280] hover:text-[#111827] ${FOCUS_RING} rounded`}>Use different number</button>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 px-3 py-2.5 bg-gray-50 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280]">
          <span className="text-base">{'🇺🇸'}</span> +1
        </span>
        <input
          type="tel"
          inputMode="numeric"
          value={digits.length > 0 ? formatPhone(digits) : ''}
          onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
          placeholder="(555) 555-1234"
          className="flex-1 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
        />
      </div>
      {error && <p className="text-[#EF4444] text-sm">{error}</p>}
      <button
        onClick={() => void handleSendCode()}
        disabled={digits.length < 10 || loading}
        className={`w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition flex items-center justify-center gap-2 ${FOCUS_RING}`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> : <Phone className="w-4 h-4" />}
        Send verification code
      </button>
      <button onClick={() => setExpanded(false)} className={`w-full text-sm text-[#6B7280] hover:text-[#111827] ${FOCUS_RING} rounded`}>Cancel</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared auth form — email magic link + Google + Phone.
//
// This is the single wired set of handlers. BOTH the mobile auth state and the
// desktop right panel render this component, so the side effects (typing-cadence
// tracker, UA attribution, device-fingerprint fraud signals via PhoneLoginSection,
// onboarding nav) live in exactly one place and cannot drift. The `variant` prop
// only changes layout: mobile stacks Google/Phone full-width; desktop lays them
// out as two side-by-side pills.
// ---------------------------------------------------------------------------

// Tracks the lg breakpoint (Tailwind lg = 1024px) so only the VISIBLE AuthForm
// variant runs its mount-time side effects. Both variants are always in the DOM
// (CSS hidden ≠ unmounted), so without this the hidden copy would also attach a
// typing-cadence tracker. SSR-safe + jsdom-safe (matchMedia may be undefined).
function useIsLgUp(): boolean {
  const query = '(min-width: 1024px)';
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return matches;
}

interface AuthFormProps {
  variant: 'mobile' | 'desktop';
  idPrefix: string;
  // Only the visible variant should run mount-time side effects (the
  // typing-cadence tracker). The handlers below stay shared/wired regardless.
  active: boolean;
  email: string;
  setEmail: (v: string) => void;
  error: string | null;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  signInWithGoogle: () => void;
  navigate: ReturnType<typeof useNavigate>;
}

function AuthForm({ variant, idPrefix, active, email, setEmail, error, loading, onSubmit, signInWithGoogle, navigate }: AuthFormProps) {
  const isDesktop = variant === 'desktop';
  const formRef = useRef<HTMLFormElement>(null);
  const trackerRef = useRef<{ getData: () => TypingCadenceData; destroy: () => void } | null>(null);

  // Attach the typing-cadence fraud tracker to this form on mount — but ONLY for
  // the visible variant (`active`). The hidden sibling layout is in the DOM but
  // must not run this side effect, else the tracker (and any fraud-signal POST)
  // fires twice. Handlers (magic-link/Google/Phone) remain shared and wired in
  // both instances; only this mount effect is gated.
  useEffect(() => {
    if (!active) return;
    if (formRef.current && !trackerRef.current) {
      trackerRef.current = createTypingCadenceTracker(formRef.current);
    }
    return () => { trackerRef.current?.destroy(); trackerRef.current = null; };
  }, [active]);

  const emailId = `${idPrefix}-email`;
  const focusEmail = () => formRef.current?.querySelector<HTMLInputElement>('input[type="email"]')?.focus();

  const googleButton = (
    <button
      type="button"
      onClick={signInWithGoogle}
      className={`${isDesktop ? 'flex-1' : 'w-full'} py-3 px-6 border-2 border-[#E5E7EB] text-[#111827] font-medium rounded-full hover:bg-gray-50 transition flex items-center justify-center gap-2 ${FOCUS_RING}`}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {isDesktop ? 'Google' : 'Continue with Google'}
    </button>
  );

  return (
    <>
      {/* A1 — one-tap options first: lowest friction, especially on return
          visits, so they lead the hierarchy. Google + Phone side-by-side on
          desktop, stacked on mobile. */}
      {isDesktop ? (
        <div className="flex gap-2.5">
          {googleButton}
          <div className="flex-1">
            <PhoneLoginSection navigate={navigate} compact />
          </div>
        </div>
      ) : (
        <>
          {googleButton}
          <PhoneLoginSection navigate={navigate} />
        </>
      )}

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-[#E5E7EB]" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-[#6B7280]">or use email</span>
        </div>
      </div>

      {/* Email magic link — the branded primary action, kept prominent below the
          one-tap options. */}
      <form ref={formRef} onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor={emailId} className="block text-sm font-medium text-[#111827] mb-1">
            Email address
          </label>
          <input
            id={emailId}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100 text-sm"
            placeholder="you@example.com"
          />
        </div>

        {error && <p className="text-[#EF4444] text-sm">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3 px-6 bg-[#5B4FE8] text-white font-medium rounded-full hover:bg-[#4338CA] disabled:opacity-50 transition ${FOCUS_RING}`}
        >
          {loading ? 'Sending...' : 'Send magic link'}
        </button>
      </form>

      <p className="text-center text-xs text-[#6B7280] mt-6">
        By continuing, you agree to our{' '}
        <a href="/terms" className="text-[#5B4FE8] hover:underline">Terms of Service</a>
        {' '}and{' '}
        <a href="/privacy" className="text-[#5B4FE8] hover:underline">Privacy Policy</a>.
      </p>
      <p className="text-center text-xs text-gray-500 mt-2">
        SMS notifications are optional. See our{' '}
        <Link to="/sms-terms" className="text-[#5B4FE8] hover:underline">SMS Terms</Link>
        {' '}for details.
      </p>

      {isDesktop && (
        <p className="text-center text-xs text-[#9CA3AF] mt-4">
          Already a member?{' '}
          <button
            type="button"
            onClick={focusEmail}
            className={`text-[#5B4FE8] font-semibold hover:underline rounded ${FOCUS_RING}`}
          >
            Sign in
          </button>
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop marketing tab bar + hero (left panel only). Content tabs, not nav:
// buttons (keyboard-accessible), underline-active, no routing.
// ---------------------------------------------------------------------------

function DesktopTabBar({
  activeTab,
  onSelect,
}: {
  activeTab: LandingTabKey;
  onSelect: (k: LandingTabKey) => void;
}) {
  return (
    <div className="flex items-center gap-5" role="tablist" aria-label="What Swipe Opinion offers">
      {LANDING_TABS.map((t) => {
        const isActive = t.key === activeTab;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(t.key)}
            className={`pb-1.5 text-sm font-medium border-b-2 transition-colors rounded-sm ${FOCUS_RING} ${
              isActive
                ? 'text-white border-white'
                : 'text-white/55 border-transparent hover:text-white/90'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function MarketingHero({ tab }: { tab: LandingTab }) {
  return (
    <div className="flex flex-col gap-9">
      <div>
        <p className="text-[40px] font-bold leading-[1.05] tracking-[-0.02em] mb-3.5">{tab.headline}</p>
        <p className="text-base leading-[1.55] opacity-90 max-w-[42ch]">{tab.subhead}</p>
      </div>

      {/* Supporting content swaps with the tab. */}
      {tab.useProof && (
        <div className="flex flex-col gap-3">
          {PROOF.map(({ Icon, text }) => (
            <div key={text} className="flex items-center gap-3">
              <span className="w-[34px] h-[34px] rounded-[9px] bg-white/[0.18] flex items-center justify-center flex-shrink-0">
                <Icon className="w-[17px] h-[17px]" />
              </span>
              <span className="text-sm">{text}</span>
            </div>
          ))}
        </div>
      )}

      {tab.bullets && (
        <ul className="flex flex-col gap-3">
          {tab.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="w-[26px] h-[26px] mt-0.5 rounded-full bg-white/[0.18] flex items-center justify-center flex-shrink-0">
                <Check className="w-[15px] h-[15px]" />
              </span>
              <span className="text-sm leading-[1.45] max-w-[40ch]">{b}</span>
            </li>
          ))}
        </ul>
      )}

      {tab.brandChips && (
        <div className="flex flex-wrap items-center gap-2">
          {tab.brandChips.map((chip, i) => (
            <span
              key={chip}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${
                i === tab.brandChips!.length - 1
                  ? 'bg-white/[0.08] text-white/70'
                  : 'bg-white/[0.16] text-white'
              }`}
            >
              {chip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login Page
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const navigate = useNavigate();
  const { signInWithMagicLink, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // T8 — value-first first impression (mobile only): the entry screen sells
  // value, and the auth methods are deferred to a second step behind one primary
  // CTA. On desktop (lg+) the split screen shows value + the form together, so
  // there is no intro/auth two-step there.
  const [mode, setMode] = useState<'intro' | 'auth'>('intro');
  // Desktop-only marketing tab (content swap; no routing). Mobile ignores this.
  const [activeTab, setActiveTab] = useState<LandingTabKey>('home');
  const activeLandingTab = LANDING_TABS.find((t) => t.key === activeTab) ?? LANDING_TABS[0];
  // Which layout is actually visible — used to run mount-time side effects in
  // exactly one AuthForm instance (the visible one), never the CSS-hidden copy.
  const isLgUp = useIsLgUp();

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Shared, single set of wired handlers/state passed to every AuthForm instance.
  const authFormProps = {
    email, setEmail, error, loading,
    onSubmit: handleMagicLink, signInWithGoogle, navigate,
  };

  // ── Sent (unchanged — full-page card on both mobile and desktop) ────────────
  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#5B4FE8] via-[#4338CA] to-teal-500 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-white/5" />
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-lg text-center mx-4">
          <h2 className="text-2xl font-bold text-[#111827] mb-2">Check your email</h2>
          <p className="text-[#6B7280]">We sent a magic link to <strong>{email}</strong></p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ════════════ MOBILE / below lg — existing two-step intro→auth flow ════════════ */}
      <div data-testid="mobile-login" className="lg:hidden">
        {mode === 'intro' ? (
          // ── Intro (value-first) ──────────────────────────────────────────────
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#5B4FE8] via-[#4338CA] to-teal-500 relative overflow-hidden">
            <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-white/10" />
            <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-white/5" />
            <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-2xl bg-white/10 rotate-12" />

            <div className="max-w-md w-full relative z-10 mx-4 text-white">
              <div className="text-center mb-6">
                <SwipeOpinionLogo
                  variant="full"
                  markVariant="white"
                  className="justify-center mb-4"
                  markClassName="w-10 h-10"
                  wordmarkClassName="text-xl tracking-tight text-white"
                />
                <h1 className="text-3xl font-bold leading-tight">{HOME_HEADLINE}</h1>
                <p className="text-purple-100 text-base mt-2">{HOME_SUBHEAD}</p>
              </div>

              {/* Proof rows */}
              <div className="space-y-2.5 mb-6">
                {PROOF.map(({ Icon, text }) => (
                  <div key={text} className="flex items-center gap-3 bg-white rounded-2xl shadow-sm px-4 py-3">
                    <div className="w-9 h-9 rounded-xl bg-[#E6F7EE] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#10B981]" />
                    </div>
                    <span className="text-sm font-semibold text-[#111827]">{text}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setMode('auth')}
                className={`w-full py-3.5 px-6 bg-white text-[#5B4FE8] font-bold rounded-full hover:bg-gray-50 transition flex items-center justify-center gap-2 shadow-lg ${FOCUS_RING}`}
              >
                Create your free account <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMode('auth')}
                className={`w-full text-center text-sm text-purple-100 hover:text-white mt-3 font-medium rounded ${FOCUS_RING}`}
              >
                Already a member? Sign in
              </button>

              <p className="flex items-center justify-center gap-1.5 text-xs text-purple-200 mt-5">
                <ShieldCheck className="w-3.5 h-3.5" /> Takes ~2 min · No purchase ever required
              </p>
            </div>
          </div>
        ) : (
          // ── Auth (existing methods, preserved) ───────────────────────────────
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#5B4FE8] via-[#4338CA] to-teal-500 relative overflow-hidden">
            <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-white/10" />
            <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-white/5" />
            <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-2xl bg-white/10 rotate-12" />
            <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-yellow-400/20" />
            <div className="absolute top-16 right-32 w-12 h-12 rounded-full bg-green-400/20" />

            <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-lg relative z-10 mx-4">
              <button
                onClick={() => setMode('intro')}
                className={`flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#111827] mb-4 rounded ${FOCUS_RING}`}
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-[#EEF2FF] flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-[#5B4FE8]" />
                </div>
                <h1 className="text-2xl font-bold text-[#111827]">
                  Welcome to Swipe <span className="text-[#5B4FE8]">Opinion</span>
                </h1>
                <p className="text-[#6B7280] text-sm mt-1">Create your account or sign in to start earning.</p>
              </div>

              <AuthForm variant="mobile" idPrefix="mobile" active={!isLgUp} {...authFormProps} />
            </div>
          </div>
        )}
      </div>

      {/* ════════════ DESKTOP (lg+) — split screen, form inline (Fix 5) ════════════ */}
      <div
        data-testid="desktop-login"
        className="hidden lg:grid lg:grid-cols-[1.1fr_1fr] min-h-screen"
      >
        {/* Left — gradient value panel (h-full fills the row; 480px floor for short viewports) */}
        <div className="relative isolate overflow-hidden bg-[linear-gradient(155deg,#5B4FE8,#4338CA_60%,#0D9488)] text-white px-11 py-12 flex flex-col h-full lg:min-h-[480px]">
          {/* Decorative blobs (static — no animation, reduced-motion safe) */}
          <div className="absolute -z-10 -top-12 -right-10 w-52 h-52 rounded-full bg-white/10" />
          <div className="absolute -z-10 -bottom-10 -left-8 w-40 h-40 rounded-full bg-white/[0.06]" />
          <div className="absolute -z-10 top-[34%] right-[18%] w-12 h-12 rounded-[13px] bg-white/10 rotate-12" />
          <div className="absolute -z-10 top-[18%] right-[40%] w-8 h-8 rounded-full bg-yellow-400/20" />
          <div className="absolute -z-10 bottom-[22%] left-[30%] w-6 h-6 rounded-full bg-emerald-400/20" />

          {/* Wordmark — brand lockup, persistent above the tabs (not per-tab) */}
          <SwipeOpinionLogo
            variant="full"
            markVariant="white"
            className="gap-2.5"
            markClassName="w-9 h-9"
            wordmarkClassName="text-2xl tracking-[-0.01em] text-white"
          />

          {/* Content tabs — swap the hero pitch below; never touch the form. */}
          <div className="mt-9">
            <DesktopTabBar activeTab={activeTab} onSelect={setActiveTab} />
          </div>

          {/* Hero — swaps per active tab; centered (my-auto) in remaining space. */}
          <div className="my-auto">
            <MarketingHero tab={activeLandingTab} />
          </div>
        </div>

        {/* Right — white auth panel */}
        <div className="bg-white px-11 py-12 flex flex-col justify-center h-full">
          <p className="text-[22px] font-bold text-[#111827] mb-1.5">Create your free account</p>
          <p className="text-[13px] text-[#6B7280] mb-6">Takes ~2 min · No purchase ever required</p>
          <AuthForm variant="desktop" idPrefix="desktop" active={isLgUp} {...authFormProps} />
        </div>
      </div>
    </>
  );
}
